import type { ILocalizedText } from '@platform/core/types';
import type { IPlatformCatalog } from './IPlatformCatalog';
import type { IPlatformModule } from './IPlatformModule';
import type { IPlatformRecordType } from './IPlatformRecordType';

type Wire = Record<string, unknown>;
const list = (v: unknown): Wire[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Wire[]) : []);
const num = (...vs: unknown[]): number | null => { const v = vs.find((x) => typeof x === 'number'); return typeof v === 'number' ? v : null; };
const bool = (...vs: unknown[]): boolean | null => { const v = vs.find((x) => typeof x === 'boolean'); return typeof v === 'boolean' ? v : null; };
const text = (...vs: unknown[]): string | null => { const v = vs.find((x) => typeof x === 'string' && x.trim()); return typeof v === 'string' ? v : null; };

function moduleOf(m: Wire): IPlatformModule {
  const disabled = bool(m.disabled);
  return {
    key: String(m.key ?? ''),
    name: (m.name && typeof m.name === 'object' ? m.name : { en: String(m.name ?? m.key ?? '') }) as ILocalizedText,
    nameOverride: text(m.nameOverride, m.name_override, m.localName, m.globalName),
    enabled: bool(m.enabled) ?? (disabled === null ? true : !disabled),
    core: !!m.core,
    platformCapability: !!m.platformCapability,
    installedIn: num(m.installedIn, m.workspaces, m.workspaceCount, m.installed) ?? 0,
  };
}

function typeOf(r: Wire, module: string): IPlatformRecordType {
  return {
    key: String(r.key ?? ''),
    module: text(r.module, r.moduleKey) ?? module,
    main: !!r.main,
    order: num(r.order, r.globalOrder),
    recordCount: num(r.recordCount, r.records, r.recordsInAllWorkspaces) ?? 0,
    overridden: bool(r.overridden, r.hasOverride, r.customized) ?? false,
    globalForm: bool(r.globalForm, r.hasGlobalForm, r.form) ?? false,
  };
}

/**
 * Відповідь GET /platform/catalog (ADR-027) у типах застосунку: модулі й типи записів — пласким переліком `recordTypes` чи вкладеними в
 * модулі; назви властивостей сервера, що мають синоніми (стан, глобальна назва, кількість записів), зводяться до одного вигляду; кількість
 * просторів із власним розміщенням полів — лише число сервера (без нього властивості немає). Чиста функція.
 */
export function platformCatalogOf(wire: unknown): IPlatformCatalog {
  const root = (wire && typeof wire === 'object' ? wire : {}) as Wire;
  const modules = list(root.modules);
  const nested = modules.flatMap((m) => list(m.recordTypes).map((r) => typeOf(r, String(m.key ?? ''))));
  const flat = list(root.recordTypes).map((r) => typeOf(r, ''));
  const catalog: IPlatformCatalog = { modules: modules.map(moduleOf), recordTypes: flat.length ? flat : nested };
  const ownLayout = num(root.workspacesWithOwnLayout);
  if (ownLayout !== null && Number.isInteger(ownLayout) && ownLayout >= 0) catalog.workspacesWithOwnLayout = ownLayout;
  return catalog;
}
